/**
 * Category Modal Component - Using FormModal with Unsaved Changes Detection
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { QuantityInput } from '../../components/form/MaskedInputs';

const categorySchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  description: z.string().optional(),
  color: z.string().min(1, 'Cor é obrigatória'),
  icon: z.string().optional(),
  order: z.number().int().min(0).optional(),
  parent_id: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
});

const COLORS = [
  '#f97316', '#ef4444', '#ec4899', '#8b5cf6', 
  '#3b82f6', '#06b6d4', '#10b981', '#84cc16',
  '#eab308', '#78716c', '#64748b', '#1e293b',
];

const ICONS = [
  'Package', 'Utensils', 'Wine', 'IceCream', 
  'Sandwich', 'UtensilsCrossed', 'Coffee', 'Pizza',
];

export function CategoryModal({ open, onOpenChange, category, categories = [], onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!category?.id;

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      color: '#f97316',
      icon: 'Package',
      order: 0,
      parent_id: null,
      is_active: true,
    },
  });

  const selectedColor = watch('color');

  // Reset form when modal opens or category changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (category) {
      reset({
        name: category.name || '',
        description: category.description || '',
        color: category.color || '#f97316',
        icon: category.icon || 'Package',
        order: category.order || 0,
        parent_id: category.parent_id || null,
        is_active: category.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        description: '',
        color: '#f97316',
        icon: 'Package',
        order: 0,
        parent_id: null,
        is_active: true,
      });
    }
  }, [open, category, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    // Clean parent_id if it's the placeholder value
    if (data.parent_id === 'none') {
      data.parent_id = null;
    }
    onSave(data);
  };

  // Filter out self from parent options when editing
  const availableParents = categories.filter(c => c.id !== category?.id);

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('categories.editCategory') : t('categories.newCategory')}
      description={isEditing ? 'Atualize os dados da categoria' : 'Preencha os dados da nova categoria'}
      size="default"
      formId="category"
      entityId={category?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">{t('categories.name')} *</Label>
          <Input 
            id="name" 
            {...register('name')} 
            placeholder="Nome da categoria"
            data-testid="category-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição</Label>
          <Textarea 
            id="description" 
            {...register('description')} 
            placeholder="Descrição da categoria"
            rows={2}
          />
        </div>

        {/* Parent Category (Subcategory support) */}
        {availableParents.length > 0 && (
          <div className="space-y-2">
            <Label>Categoria Pai (opcional)</Label>
            <Controller
              name="parent_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || 'none'}
                  onValueChange={(val) => field.onChange(val === 'none' ? null : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma (categoria principal)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma (categoria principal)</SelectItem>
                    {availableParents.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-xs text-muted-foreground">
              Selecione uma categoria pai para criar uma subcategoria
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>{t('categories.color')} *</Label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  selectedColor === color 
                    ? 'border-primary scale-110 ring-2 ring-primary/30' 
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setValue('color', color)}
              />
            ))}
          </div>
          {errors.color && <p className="text-sm text-destructive">{errors.color.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('categories.icon')}</Label>
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um ícone" />
                  </SelectTrigger>
                  <SelectContent>
                    {ICONS.map(icon => (
                      <SelectItem key={icon} value={icon}>
                        {icon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="order">{t('categories.order')}</Label>
            <Controller
              name="order"
              control={control}
              render={({ field }) => (
                <QuantityInput
                  id="order"
                  value={field.value}
                  onValueChange={field.onChange}
                  min={0}
                />
              )}
            />
          </div>
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default CategoryModal;
